import { useState } from "react";
import Card from "components/card";

const KINDS = [
  {
    key: "oneoff",
    name: "One-off Yes/No",
    when: "A single question with a date: will a bill pass, will a team win on Saturday.",
    example: "Will the Finance Bill 2027 be signed by 30 June?",
  },
  {
    key: "race",
    name: "Race (several answers)",
    when: "Several possible answers to one question. Each answer trades as its own Yes/No market, grouped on one page.",
    example: "Who will win the Nairobi governor race? Sakaja, Owino, Kagwe...",
  },
  {
    key: "recurring",
    name: "Recurring (daily, weekdays, weekly)",
    when: "The same question on a schedule. A fresh market is created for every day (or chosen days) automatically.",
    example: "Who will be on the Daily Nation front page on {date}?",
  },
  {
    key: "short",
    name: "Short-term (every 5, 10, 15, 30 or 60 minutes)",
    when: "Fast questions that settle in minutes: a price at the end of the window, a score, a live event. Markets run back to back.",
    example: "Will Safaricom trade above KES 30 at {end}?",
  },
  {
    key: "source",
    name: "From a source",
    when: "Start from a news story or fixture already in the system and edit it into a market.",
    example: "A Premier League fixture becomes 'Will Gor Mahia beat AFC Leopards?'",
  },
];

const STEPS = {
  admin: {
    oneoff: [
      "All Markets, Create Market.",
      "Write the question, the rules, the close time and the resolution source.",
      "Save, then approve it from the Approval Queue with a second operator (the person who wrote it cannot approve it).",
    ],
    race: [
      "Optional: Series & Races, Clusters, Create Cluster, to group related races on one hub page.",
      "Series & Races, Markets, Create Market. Pick the cluster, write the question, then list the answers (at least two: candidates, parties or free text).",
      "Set the schedule and rules, then approve from the Approval Queue with a second operator. Each answer trades as its own Yes/No market.",
      "To load many races at once use Bulk Create Markets with the CSV template.",
    ],
    recurring: [
      "Series & Races, Series, Create Series.",
      "Step 1: name and question. Use {date} where the day should appear.",
      "Step 2: answers. Fixed list, or pulled from Reference Data (parties, people, NSE securities).",
      "Step 3: timing. Choose Every day, Weekdays, Trading days or Weekly, then open, close and result times. The preview shows today's real timestamps.",
      "Step 4: resolver. Manual for anything a person judges; Market data for NSE prices (resolves automatically).",
      "Step 5: automation. Leave generate, publish, close and settle on. Turn auto-resolve on only with an automatic resolver.",
      "Submit, then a second operator opens the series and clicks Approve and activate. Markets start generating on the next tick.",
    ],
    short: [
      "Series & Races, Series, Create Series.",
      "Question: use {time}, {end} or {window} for the window's clock times, for example 'Will SCOM be above KES 30 at {end}?'.",
      "Timing: choose Short-term (every few minutes), then the window (5, 10, 15, 20, 30 or 60 minutes) and how many minutes after the close the result is expected (0 = at the close).",
      "Build buffer: 1 or 2 minutes for a 5-minute series. It must be shorter than the window. The next window opens the moment the current one closes, so the board is never empty.",
      "Resolver: Manual. Every window then lands in the Resolution Center at its close for an operator to settle.",
      "Submit and approve with a second operator. The first market is the window running now; windows line up with the clock (10:00, 10:05, 10:10).",
    ],
    source: [
      "Market Ingestion lists candidates from news and fixture feeds.",
      "Open one, edit the question and rules, then publish it into the Approval Queue.",
    ],
  },
  partner: {
    oneoff: [
      "Markets, Create Market.",
      "Write the question, answers, close time and how it will be resolved. Your draft saves itself as you type.",
      "Submit. Your tenant reviews it and it goes live once approved.",
    ],
    race: [
      "Create Market makes a single Yes/No question. For a question with several answers on a schedule, use Recurring Markets and put each answer on its own line: every answer becomes its own Yes/No market.",
      "For a one-off race with several answers (an election, a contest), ask your tenant to set it up; it is then listed on your Markets page like any other market.",
    ],
    recurring: [
      "Recurring Markets, New recurring market, choose Every day, Weekdays or Weekly.",
      "Write the question with {date} where the day goes, and list the answers (one per line).",
      "Set open, close and result times. Use a result day offset of 1 if the result lands after midnight.",
      "Say where the result comes from and write the rule. Send for approval. Markets start once your tenant approves.",
    ],
    short: [
      "Recurring Markets, New recurring market, choose Short-term.",
      "Pick how long each market runs (5, 10, 15, 20, 30 or 60 minutes).",
      "Write the question with {end} (or {time}, {window}) for the window's clock time. One answer 'Yes' makes a simple Yes/No question.",
      "Say where the result comes from and write an exact rule, then Send for approval.",
      "Once approved a new market opens every window, back to back. Pause or resume the series from the same page at any time.",
    ],
    source: [
      "Markets, From a Source.",
      "Pick a story or fixture, edit it into a question, and submit it like any other market.",
    ],
  },
};

function Section({ title, children }) {
  return (
    <Card extra="p-5">
      <p className="text-lg font-semibold text-navy-700 dark:text-white">{title}</p>
      <div className="mt-3 space-y-2 text-sm leading-relaxed text-gray-700 dark:text-gray-300">{children}</div>
    </Card>
  );
}

export default function MarketGuide({ portal = "partner" }) {
  const [kind, setKind] = useState("short");
  const steps = STEPS[portal][kind];
  return (
    <div className="flex flex-col gap-5">
      <Section title="Creating markets: a guide">
        <p>
          Every market on Wasaa is a question with a clear answer, a time it stops trading, and a rule for how it will be decided. Pick the kind
          that fits your question, then follow the steps for this portal.
        </p>
      </Section>

      <Section title="1. Pick the kind of market">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {KINDS.map((k) => (
            <button
              key={k.key}
              type="button"
              onClick={() => setKind(k.key)}
              className={`rounded-sm border p-4 text-left transition ${kind === k.key ? "border-brand-500 bg-brand-500/5" : "border-gray-200 hover:border-brand-500/50 dark:border-white/10"}`}
            >
              <p className="font-semibold text-navy-700 dark:text-white">{k.name}</p>
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{k.when}</p>
              <p className="mt-2 text-xs italic text-gray-500">{k.example}</p>
            </button>
          ))}
        </div>
      </Section>

      <Section title={`2. Steps: ${KINDS.find((k) => k.key === kind).name}`}>
        <ol className="list-decimal space-y-2 pl-5">
          {steps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
      </Section>

      <Section title="3. Writing a question that resolves cleanly">
        <ul className="list-disc space-y-1 pl-5">
          <li>Name the source and the exact moment: "the last traded price on the NSE at 10:05", not "the price".</li>
          <li>Say what happens in the edge cases: a tie, a postponed match, a source that does not publish.</li>
          <li>
            Placeholders in recurring questions: <b>{"{date}"}</b> is the day (25 September 2026). For short-term series <b>{"{time}"}</b> is the
            window's start, <b>{"{end}"}</b> its end and <b>{"{window}"}</b> both (10:05 to 10:10).
          </li>
          <li>Each answer you list becomes its own Yes/No market. People can buy Yes or No on every answer.</li>
        </ul>
      </Section>

      <Section title="4. Approval">
        <p>
          Nothing goes live without approval. {portal === "partner" ? "Your tenant reviews every market and series you submit." : "The operator who creates a market or series cannot approve it; a second operator must."} A recurring or short-term series is approved once; after that its markets are created, opened and closed automatically on schedule.
        </p>
      </Section>

      <Section title="5. How markets are resolved">
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <b>Manual (most markets, including every short-term series):</b> at the close the market moves to the Resolution Center. An operator
            picks the correct answer with evidence, and the market settles and pays out automatically straight after.
          </li>
          <li>
            <b>Automatic:</b> available where the result is published as data. Today that is the NSE market-data resolver (for example "biggest gainer
            of the day"). If the data is missing or ambiguous it never guesses: the market goes to the Resolution Center for a person.
          </li>
          <li>Set the "result expected" delay to when the answer is actually knowable. The system asks at that time and retries until it can.</li>
          {portal === "partner" && <li>Partners do not resolve markets. This keeps every result independent of the platform running it.</li>}
        </ul>
      </Section>

      <Section title="6. Where the money goes">
        <ul className="list-disc space-y-1 pl-5">
          <li>Every correct share pays KES 1.00. Losing shares pay nothing.</li>
          <li>Correct traders pay a 10% commission on their gain only, never on the stake they put in.</li>
          <li>Losing stakes left in the pool after correct traders are paid are booked as settlement revenue.</li>
          <li>If nobody holds the correct side of a market, that market is voided and everyone in it is refunded.</li>
          <li>
            New markets start at 50/50 with no house money in the pool, so the very first buyer pays close to KES 1.00 a share. Prices become fair
            as both sides trade.
          </li>
          <li>Net revenue is split 80% to the tenant and 20% to the platform; the KES 1 SMS fee on each deposit goes to the tenant in full. A partner receives the share its tenant agreed (30% unless changed) of the tenant's 80% of what the partner's own markets earn.</li>
        </ul>
      </Section>

      {portal === "admin" && (
        <Section title="7. Testing a short-term series end to end">
          <p>
            Against a local API started with <code>MARKET_AUTOMATION_INTERVAL_SECONDS=5</code>, run{" "}
            <code>pnpm --filter @winnatrader/api exec tsx scripts/short-term-markets.ts</code>. It creates a 5-minute and a 10-minute series,
            approves them with two operators, funds eight traders through the sandbox rail, trades Yes and No, waits for the close, resolves,
            and checks every wallet, pool and commission to the cent.
          </p>
        </Section>
      )}
    </div>
  );
}
